/**
 * Money in Momonos is always an integer number of BDT (taka).
 * Poisha are not used in restaurant pricing, so there is no minor unit.
 *
 * SRS §4 / ADR-6. Float money is a bug class we refuse to have.
 */

export type Money = number

/** Rate expressed in basis points: 500 = 5.00%, 1500 = 15.00%. */
export type BasisPoints = number

export const BASIS_POINT_SCALE = 10_000

function assertMoney(value: number, label = 'value'): asserts value is Money {
  if (!Number.isInteger(value)) {
    throw new TypeError(`${label} must be an integer BDT amount, received ${value}`)
  }
}

/**
 * Round half away from zero (the intuitive "0.5 rounds up" behaviour).
 * JS Math.round rounds -0.5 to -0 which is wrong for refunds/discounts.
 */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

export function addMoney(a: Money, b: Money): Money {
  assertMoney(a, 'a')
  assertMoney(b, 'b')
  return a + b
}

export function subtractMoney(a: Money, b: Money): Money {
  assertMoney(a, 'a')
  assertMoney(b, 'b')
  return a - b
}

export function multiplyMoney(amount: Money, quantity: number): Money {
  assertMoney(amount, 'amount')
  if (!Number.isInteger(quantity)) {
    throw new TypeError(`quantity must be an integer, received ${quantity}`)
  }
  return amount * quantity
}

export function sumMoney(amounts: readonly Money[]): Money {
  return amounts.reduce<Money>((total, amount) => addMoney(total, amount), 0)
}

/** Percentage of an amount, expressed in basis points, rounded half up. */
export function applyPercent(amount: Money, rate: BasisPoints): Money {
  assertMoney(amount, 'amount')
  return roundHalfUp((amount * rate) / BASIS_POINT_SCALE)
}

export interface VatSplit {
  /** Amount excluding VAT. */
  net: Money
  /** VAT portion. */
  vat: Money
  /** Amount including VAT. */
  gross: Money
}

/** Menu price excludes VAT; VAT is added at billing. */
export function splitVatExclusive(net: Money, rate: BasisPoints): VatSplit {
  assertMoney(net, 'net')
  const vat = applyPercent(net, rate)
  return { net, vat, gross: net + vat }
}

/**
 * Menu price already includes VAT; the VAT portion is extracted.
 * net is derived by subtraction so net + vat === gross always holds.
 */
export function splitVatInclusive(gross: Money, rate: BasisPoints): VatSplit {
  assertMoney(gross, 'gross')
  const net = roundHalfUp((gross * BASIS_POINT_SCALE) / (BASIS_POINT_SCALE + rate))
  return { net, vat: gross - net, gross }
}

const BENGALI_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'] as const

export function toBengaliDigits(input: string): string {
  return input.replace(/[0-9]/g, (digit) => BENGALI_DIGITS[Number(digit)] ?? digit)
}

/** Display formatting. NFR-7: BDT with the ৳ sign, Bengali numerals in bn. */
export function formatBdt(amount: Money, locale: 'en' | 'bn' = 'en'): string {
  const grouped = new Intl.NumberFormat('en-US').format(amount)
  return `৳${locale === 'bn' ? toBengaliDigits(grouped) : grouped}`
}
